import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack,
  School,
  CalendarMonth,
  Print,
  EmojiEvents,
  Image,
  PictureAsPdf,
  Edit,
  Add,
} from '@mui/icons-material';
import { adminAPI, teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ResultSheet from '../../components/ui/ResultSheet';
import PrimaryResultSheet from '../../components/ui/PrimaryResultSheet';
import KGResultSheet from '../../components/ui/KGResultSheet';

const GRADES = ['A', 'B', 'C', 'D', 'E', 'F'];

const isKGLevel = (resultData) => {
  const classInfo = resultData?.Class || resultData?.class || {};
  const schoolLevel = classInfo.schoolLevel || classInfo.SchoolLevel || '';
  const className = classInfo.name || classInfo.Name || classInfo.displayName || '';
  const kgLevels = ['Preparatory', 'KG', 'Kindergarten', 'Pre-K', 'Preprimary'];
  return [...kgLevels, 'Creche', 'Daycare'].some(level =>
    schoolLevel.toLowerCase().includes(level.toLowerCase()) ||
    className.toLowerCase().includes(level.toLowerCase())
  );
};

const isPrimaryLevel = (resultData) => {
  if (isKGLevel(resultData)) return false;
  const classInfo = resultData?.Class || resultData?.class || {};
  const schoolLevel = classInfo.schoolLevel || classInfo.SchoolLevel || '';
  const className = classInfo.name || classInfo.Name || classInfo.displayName || '';
  const primaryLevels = ['Primary', 'Nursery'];
  return primaryLevels.some(level =>
    schoolLevel.toLowerCase().includes(level.toLowerCase()) ||
    className.toLowerCase().includes(level.toLowerCase())
  );
};

const StudentResult = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const location = useLocation();
  const { hasRole } = useAuth();

  const basePath = hasRole('Admin') ? '/admin-dashboard' : '/teacher-dashboard';
  
  const isTeacher = hasRole('Teacher');
  
  // Use teacherAPI for teachers, adminAPI for admins
  const resultsAPI = isTeacher ? teacherAPI : adminAPI;

  // Get termId and classId from query params
  const searchParams = new URLSearchParams(location.search);
  const termIdFromQuery = searchParams.get('termId');
  const classIdFromQuery = searchParams.get('classId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resultList, setResultList] = useState([]);
  const [student, setStudent] = useState(null);
  const [term, setTerm] = useState(null);
  const [academicYear, setAcademicYear] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const downloadTemplateRef = useRef(null);

  const [psychomotorModalOpen, setPsychomotorModalOpen] = useState(false);
  const [psychomotorData, setPsychomotorData] = useState({
    drawingPainting: '',
    gamesSports: '',
    handwriting: '',
    verbalFluency: '',
    musicalSkills: '',
    handlingTools: '',
  });
  const [affectiveData, setAffectiveData] = useState({
    punctuality: '',
    politeness: '',
    perseverance: '',
    honesty: '',
    health: '',
    helpingOthers: '',
    attentiveness: '',
    emotionalStability: '',
    attitudeToClassWork: '',
    speakingHandwriting: '',
    spiritOfCooperation: '',
  });
  const [savingPsychomotor, setSavingPsychomotor] = useState(false);

  const handleDownloadImage = async () => {
    if (!downloadTemplateRef.current || downloading) {
      console.error('Download error: Ref is null or downloading');
      return;
    }
    
    setDownloading(true);
    try {
      const element = downloadTemplateRef.current;
      console.log('Capturing element:', element);
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: true,
      });
      console.log('Canvas created:', canvas);
      
      const link = document.createElement('a');
      link.download = `Result_${student?.firstName || 'Student'}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      console.log('Download triggered');
      link.click();
    } catch (err) {
      console.error('Error downloading image:', err);
      setError('Failed to download image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!downloadTemplateRef.current || downloading) {
      console.error('Download error: Ref is null or downloading');
      return;
    }
    
    setDownloading(true);
    try {
      const element = downloadTemplateRef.current;
      console.log('Capturing element for PDF:', element);
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: true,
      });
      console.log('Canvas created for PDF:', canvas);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      console.log('Adding image to PDF, dimensions:', pdfWidth, pdfHeight);
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      console.log('PDF created, saving...');
      pdf.save(`Result_${student?.firstName || 'Student'}_${Date.now()}.pdf`);
      console.log('PDF download triggered');
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Helper to get value from object with case-insensitive key matching
  const getTraitValue = (obj, key) => {
    if (!obj) return '';
    // Try exact key first
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    // Try lowercase
    const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
    if (obj[lowerKey] !== undefined && obj[lowerKey] !== null) return obj[lowerKey];
    // Try PascalCase
    const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
    if (obj[pascalKey] !== undefined && obj[pascalKey] !== null) return obj[pascalKey];
    return '';
  };

  // Helper to get all trait values from a source object (handles both camelCase and PascalCase)
  const getTraitObject = (result, camelKey, pascalKey) => {
    return result?.[camelKey] || result?.[pascalKey] || {};
  };

  const handleOpenPsychomotorModal = () => {
    const psychomotor = getTraitObject(result, 'psychomotorSkills', 'PsychomotorSkills');
    const affective = getTraitObject(result, 'affectiveTraits', 'AffectiveTraits');
    
    console.log('=== Modal Open ===');
    console.log('Raw psychomotor from result:', JSON.stringify(psychomotor));
    console.log('Raw affective from result:', JSON.stringify(affective));
    
    setPsychomotorData({
      drawingPainting: getTraitValue(psychomotor, 'drawingPainting'),
      gamesSports: getTraitValue(psychomotor, 'gamesSports'),
      handwriting: getTraitValue(psychomotor, 'handwriting'),
      verbalFluency: getTraitValue(psychomotor, 'verbalFluency'),
      musicalSkills: getTraitValue(psychomotor, 'musicalSkills'),
      handlingTools: getTraitValue(psychomotor, 'handlingTools'),
    });
    
    setAffectiveData({
      punctuality: getTraitValue(affective, 'punctuality'),
      politeness: getTraitValue(affective, 'politeness'),
      perseverance: getTraitValue(affective, 'perseverance'),
      honesty: getTraitValue(affective, 'honesty'),
      health: getTraitValue(affective, 'health'),
      helpingOthers: getTraitValue(affective, 'helpingOthers'),
      attentiveness: getTraitValue(affective, 'attentiveness'),
      emotionalStability: getTraitValue(affective, 'emotionalStability'),
      attitudeToClassWork: getTraitValue(affective, 'attitudeToClassWork'),
      speakingHandwriting: getTraitValue(affective, 'speakingHandwriting'),
      spiritOfCooperation: getTraitValue(affective, 'spiritOfCooperation'),
    });
    
    setPsychomotorModalOpen(true);
  };

  const handleSavePsychomotorAffective = async () => {
    if (!result?.id) return;
    
    setSavingPsychomotor(true);
    try {
      console.log('Saving psychomotor/affective:', { psychomotorData, affectiveData });
      const response = await resultsAPI.results.updatePsychomotorAffective(result.id, {
        psychomotorSkills: psychomotorData,
        affectiveTraits: affectiveData,
      });
      
      console.log('Save response:', response.data);
      
      if (response.data?.success) {
        setPsychomotorModalOpen(false);
        await fetchStudentResult();
      } else {
        setError(response.data?.message || 'Failed to save psychomotor and affective traits');
      }
    } catch (err) {
      console.error('Error saving psychomotor/affective:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSavingPsychomotor(false);
    }
  };

  const handlePsychomotorChange = (field, value) => {
    setPsychomotorData(prev => ({ ...prev, [field]: value }));
  };

  const handleAffectiveChange = (field, value) => {
    setAffectiveData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (studentId && termIdFromQuery) {
      fetchStudentResult();
    } else if (studentId) {
      setError('Term ID is required');
      setLoading(false);
    }
  }, [studentId, termIdFromQuery, classIdFromQuery]);

  const fetchStudentResult = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch student results using ResultController endpoint ONLY
      // Admin/Teacher can see unpublished results, parents can only see published
      const includeUnpublished = hasRole('Admin') || hasRole('Teacher');
      const response = await resultsAPI.results.getByStudentAndTerm(studentId, termIdFromQuery, includeUnpublished, classIdFromQuery);

      if (response.data?.success) {
        const results = response.data.data || [];
        setResultList(results);

        if (results.length > 0) {
          const firstResult = results[0];
          
          // Extract student info from the result (provided by ResultsController)
          if (firstResult.student) {
            setStudent(firstResult.student);
          } else if (firstResult.Student) {
            setStudent(firstResult.Student);
          }
          
          // Extract term info
          if (firstResult.term) {
            setTerm(firstResult.term);
          } else if (firstResult.Term) {
            setTerm(firstResult.Term);
          }
          
          // Extract academic year info
          if (firstResult.academicYear) {
            setAcademicYear(firstResult.academicYear);
          } else if (firstResult.AcademicYear) {
            setAcademicYear(firstResult.AcademicYear);
          }
        }
      } else {
        setError(response.data?.message || 'No results found for this student');
      }
    } catch (err) {
      console.error('Error fetching student result:', err);
      setError('Failed to fetch student result. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get nested value with case-insensitive keys
  const getValue = (obj, ...keys) => {
    if (!obj) return null;
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
      // Try lowercase
      const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
      if (obj[lowerKey] !== undefined) return obj[lowerKey];
      // Try original case from backend (PascalCase)
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
      if (obj[pascalKey] !== undefined) return obj[pascalKey];
    }
    return null;
  };

  const getGradeColor = (grade) => {
    if (!grade) return { bgcolor: '#F5F5F5', color: '#757575' };
    const firstChar = grade.toUpperCase().charAt(0);
    if (['A'].includes(firstChar)) return { bgcolor: '#E8F5E9', color: '#2E7D32' };
    if (['B'].includes(firstChar)) return { bgcolor: '#E3F2FD', color: '#1565C0' };
    if (['C'].includes(firstChar)) return { bgcolor: '#FFF3E0', color: '#F57C00' };
    if (['D'].includes(firstChar)) return { bgcolor: '#FFEBEE', color: '#C62828' };
    if (['F'].includes(firstChar)) return { bgcolor: '#FFEBEE', color: '#C62828' };
    return { bgcolor: '#F5F5F5', color: '#757575' };
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 70) return { color: '#2E7D32', bgcolor: '#E8F5E9', label: 'Excellent' };
    if (percentage >= 60) return { color: '#1976D2', bgcolor: '#E3F2FD', label: 'Good' };
    if (percentage >= 50) return { color: '#F57C00', bgcolor: '#FFF3E0', label: 'Fair' };
    if (percentage >= 40) return { color: '#F57C00', bgcolor: '#FFF3E0', label: 'Pass' };
    return { color: '#C62828', bgcolor: '#FFEBEE', label: 'Needs Improvement' };
  };

  const handlePrint = () => {
    window.print();
  };

  // Get the first result (should only be one per student per term)
  const result = resultList.length > 0 ? resultList[0] : null;

  // Get subject results - handle both camelCase and PascalCase
  const subjectResults = result 
    ? (result.subjectResults || result.SubjectResults || [])
    : [];

  // Detect if this is a KG student
  const classInfo = result?.Class || result?.class || {};
  const schoolLevel = classInfo.schoolLevel || classInfo.SchoolLevel || '';
  const className = classInfo.name || classInfo.Name || classInfo.displayName || '';
  const isKGStudent = ['Preparatory', 'KG', 'Kindergarten', 'Pre-K', 'Preprimary', 'Creche', 'Daycare'].some(level =>
    schoolLevel.toLowerCase().includes(level.toLowerCase()) ||
    className.toLowerCase().includes(level.toLowerCase())
  );

  // KG Assessment items mapping (each has its own unique backend key)
  const KG_ASSESSMENT_ITEMS = [
    { label: 'Ability to Identify Shapes', key: 'abilityToIdentifyShapes', group: 'psychomotor' },
    { label: 'Ability to Colour', key: 'abilityToColour', group: 'psychomotor' },
    { label: 'Ability to Communicate with Peer', key: 'abilityToCommunicateWithPeer', group: 'psychomotor' },
    { label: 'Showing Good Habit', key: 'showingGoodHabit', group: 'psychomotor' },
    { label: 'Table Manner', key: 'tableManner', group: 'psychomotor' },
    { label: 'Communication Skill & Language', key: 'communicationSkillLanguage', group: 'psychomotor' },
    { label: 'Ability to Identify & Write Letters', key: 'abilityToIdentifyWriteLetters', group: 'psychomotor' },
    { label: 'Ability to Identify Objects at School', key: 'abilityToIdentifyObjectsSchool', group: 'psychomotor' },
    { label: 'Ability to Identify Objects at Home', key: 'abilityToIdentifyObjectsHome', group: 'psychomotor' },
    { label: 'Attitude Towards Classwork', key: 'attitudeTowardsClasswork', group: 'psychomotor' },
    { label: 'Punctuality', key: 'kgPunctuality', group: 'affective' },
    { label: 'Physical Appearance', key: 'kgPhysicalAppearance', group: 'affective' },
    { label: 'Adjusting to Good Habit', key: 'kgAdjustingGoodHabit', group: 'affective' },
    { label: 'Adjusting to Good Eating Habit', key: 'kgAdjustingGoodEating', group: 'affective' },
    { label: 'Regularity in School', key: 'kgRegularityInSchool', group: 'affective' },
    { label: 'Ability to Differentiate Colour', key: 'abilityToDifferentiateColour', group: 'psychomotor' },
    { label: 'Ability to Scribble', key: 'abilityToScribble', group: 'psychomotor' },
    { label: 'Ability to Recite Rhymes', key: 'abilityToReciteRhymes', group: 'psychomotor' },
    { label: 'Sport', key: 'kgSport', group: 'affective' },
    { label: 'Ability to Clap to Tone', key: 'abilityToClapToTone', group: 'psychomotor' },
    { label: 'Ability to Dance to Music', key: 'abilityToDanceToMusic', group: 'psychomotor' },
    { label: 'Ability to Twist', key: 'abilityToTwist', group: 'psychomotor' },
    { label: 'Ability to Relate with Peer', key: 'kgAbilityToRelateWithPeer', group: 'affective' },
    { label: 'Attitude Towards Assignment', key: 'kgAttitudeTowardsAssignment', group: 'affective' },
  ];

  // Calculate totals
  const calculateTotal = () => {
    if (!subjectResults || subjectResults.length === 0) return { totalObtained: 0, totalMaximum: 0, overallPercentage: 0 };
    
    const totalObtained = subjectResults.reduce((sum, sr) => sum + (getValue(sr, 'totalScore') || 0), 0);
    const totalMaximum = subjectResults.reduce((sum, sr) => sum + (getValue(sr, 'maximumScore') || 0), 0);
    const overallPercentage = totalMaximum > 0 ? (totalObtained / totalMaximum) * 100 : 0;
    
    return { totalObtained, totalMaximum, overallPercentage };
  };

  const { totalObtained, totalMaximum, overallPercentage } = calculateTotal();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#2E7D32' }} />
      </Box>
    );
  }

  // Build student name from student object or result
  let studentName = 'Unknown Student';
  let studentNumber = 'N/A';
  
  if (student) {
    studentName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown Student';
    studentNumber = student.studentNumber || student.admissionNumber || 'N/A';
  } else if (result) {
    // Try to get from result.Student
    const studentData = result.student || result.Student;
    if (studentData) {
      studentName = `${getValue(studentData, 'firstName') || ''} ${getValue(studentData, 'lastName') || ''}`.trim() || 'Unknown Student';
      studentNumber = getValue(studentData, 'studentNumber') || getValue(studentData, 'admissionNumber') || 'N/A';
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F5F7FA 0%, #E8F5E9 100%)',
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <IconButton 
            onClick={() => navigate(`${basePath}/results`)}
            sx={{ 
              bgcolor: '#2E7D32', 
              color: 'white',
              '&:hover': { bgcolor: '#1B5E20' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B5E20' }}>
              Student Result
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={18} /> : <Image />}
            onClick={handleDownloadImage}
            disabled={downloading || !result}
            sx={{
              color: '#1976D2',
              borderColor: '#1976D2',
              mr: 1,
              '&:hover': { borderColor: '#1565C0', bgcolor: '#E3F2FD' },
            }}
          >
            Image
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={18} /> : <PictureAsPdf />}
            onClick={handleDownloadPdf}
            disabled={downloading || !result}
            sx={{
              color: '#D32F2F',
              borderColor: '#D32F2F',
              mr: 1,
              '&:hover': { borderColor: '#B71C1C', bgcolor: '#FFEBEE' },
            }}
          >
            PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#2E7D32',
              '&:hover': { bgcolor: '#1B5E20' },
            }}
          >
            Print
          </Button>
          {(hasRole('Admin') || hasRole('Teacher')) && (
            <Button
              variant="contained"
              startIcon={result?.psychomotorSkills || result?.PsychomotorSkills ? <Edit /> : <Add />}
              onClick={handleOpenPsychomotorModal}
              sx={{
                bgcolor: '#7B1FA2',
                '&:hover': { bgcolor: '#6A1B9A' },
                ml: 1,
              }}
            >
              Psychomotor & Affective
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Report Card */}
        {!error && result && (
          <Paper
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            }}
          >
            {/* Report Card Header - Green Theme */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                p: { xs: 3, sm: 4 },
                color: 'white',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {studentName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <School sx={{ fontSize: 20, opacity: 0.8 }} />
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {studentNumber}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth sx={{ fontSize: 20, opacity: 0.8 }} />
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {term?.name || term?.Name || 'N/A'}
                      </Typography>
                    </Box>
                    {academicYear && (
                      <Chip 
                        label={academicYear.name || academicYear.Name || 'N/A'}
                        size="small"
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)', 
                          color: 'white',
                          fontWeight: 600
                        }} 
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  {result.positionInClass || result.PositionInClass ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <EmojiEvents sx={{ fontSize: 32, color: '#FFD700' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Position: {result.positionInClass || result.PositionInClass}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                </Grid>
              </Grid>
            </Box>

            {/* Results Table */}
            <TableContainer sx={{ p: 0 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#E8F5E9' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Subject
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Objective Score
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Theory Score
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Test Score
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Total
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Percentage
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Grade
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20', borderBottom: '2px solid #2E7D32' }}>
                      Remarks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subjectResults && subjectResults.length > 0 ? (
                    subjectResults.map((subjectResult, index) => {
                      const objectiveScore = getValue(subjectResult, 'objectiveScore') || getValue(subjectResult, 'ObjectiveScore') || 0;
                      const theoryScore = getValue(subjectResult, 'theoryScore') || getValue(subjectResult, 'TheoryScore') || 0;
                      const testScore = getValue(subjectResult, 'testScore') || getValue(subjectResult, 'TestScore') || 0;
                      const totalScore = getValue(subjectResult, 'totalScore') || getValue(subjectResult, 'TotalScore') || 0;
                      const maximumScore = getValue(subjectResult, 'maximumScore') || getValue(subjectResult, 'MaximumScore') || 0;
                      const percentage = getValue(subjectResult, 'percentage') || getValue(subjectResult, 'Percentage') || 0;
                      const gradeLetter = getValue(subjectResult, 'gradeLetter') || getValue(subjectResult, 'GradeLetter') || 'N/A';
                      const subjectName = getValue(subjectResult, 'subjectName') || getValue(subjectResult, 'SubjectName') || 'Unknown Subject';
                      
                      // Default remarks based on grade letter (fallback if remark not in data)
                      const defaultRemarks = {
                        'A': 'Excellent',
                        'B': 'Very Good',
                        'C': 'Good',
                        'D': 'Fair',
                        'F': 'Fail'
                      };
                      const remark = getValue(subjectResult, 'remark') || getValue(subjectResult, 'Remark') || defaultRemarks[gradeLetter] || '';
                      
                      // Debug: check all keys in subjectResult
                      console.log('Subject:', subjectName, 'Keys:', Object.keys(subjectResult), 'gradeLetter:', gradeLetter, 'remark:', remark);
                      
                      const percentageColor = getPercentageColor(percentage);
                      const gradeColor = getGradeColor(gradeLetter);
                      
                      return (
                        <TableRow 
                          key={getValue(subjectResult, 'subjectId') || getValue(subjectResult, 'SubjectId') || index}
                          sx={{ 
                            background: index % 2 === 0 ? 'white' : '#FAFAFA',
                            '&:hover': { background: '#F5F7FA' }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#333' }}>
                            {subjectName}
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#555' }}>
                            {objectiveScore}
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#555' }}>
                            {theoryScore}
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#555' }}>
                            {testScore}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#1B5E20' }}>
                            {totalScore} / {maximumScore}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${percentage.toFixed(0)}%`}
                              size="small"
                              sx={{
                                bgcolor: percentageColor.bgcolor,
                                color: percentageColor.color,
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={gradeLetter}
                              size="small"
                              sx={{
                                bgcolor: gradeColor.bgcolor,
                                color: gradeColor.color,
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#666', fontStyle: remark ? 'normal' : 'italic' }}>
                            {remark || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#78909C' }}>
                          No subject results found. The student may not have completed any exams yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary Section */}
            <Box sx={{ p: 3, background: '#F5F7FA' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, border: '2px solid #2E7D32' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#78909C', mb: 1 }}>
                        Total Score
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1B5E20' }}>
                        {totalObtained} / {totalMaximum}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, border: '2px solid #2E7D32' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#78909C', mb: 1 }}>
                        Overall Percentage
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1B5E20' }}>
                        {overallPercentage.toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, border: '2px solid #2E7D32' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#78909C', mb: 1 }}>
                        Overall Grade
                      </Typography>
                      <Chip
                        label={result.overallGradeLetter || result.OverallGradeLetter || 'N/A'}
                        sx={{
                          bgcolor: getGradeColor(result.overallGradeLetter || result.OverallGradeLetter).bgcolor,
                          color: getGradeColor(result.overallGradeLetter || result.OverallGradeLetter).color,
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          height: 40,
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Performance Summary */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1B5E20', mb: 1 }}>
                    {overallPercentage >= 70 
                      ? 'Excellent Performance - Keep up the great work!' 
                      : overallPercentage >= 60 
                        ? 'Good Performance - Room for improvement'
                        : overallPercentage >= 50 
                          ? 'Fair Performance - Needs more effort'
                          : overallPercentage > 0
                            ? 'Performance Needs Improvement - Additional support recommended'
                            : 'No results available'}
                  </Typography>
                </Box>
              </Box>

              {/* Teacher Comments */}
              {(result.teacherComment || result.TeacherComment || result.teacherRemarks || result.TeacherRemarks) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#E3F2FD', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}>
                    Teacher's Comment:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    {result.teacherComment || result.TeacherComment || result.teacherRemarks || result.TeacherRemarks}
                  </Typography>
                </Box>
              )}

              {/* Behavior Comment */}
              {(result.behaviorComment || result.BehaviorComment) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#F3E5F5', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#7B1FA2', mb: 1 }}>
                    Behavior & Work Habits:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    {result.behaviorComment || result.BehaviorComment}
                  </Typography>
                </Box>
              )}

              {/* Headmaster Comment */}
              {(result.headmasterComment || result.HeadmasterComment) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF3E0', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#F57C00', mb: 1 }}>
                    Headmaster's Comment:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    {result.headmasterComment || result.HeadmasterComment}
                  </Typography>
                </Box>
              )}

              {/* Psychomotor Skills & Affective Traits Display */}
              {(result?.psychomotorSkills || result?.PsychomotorSkills || result?.affectiveTraits || result?.AffectiveTraits) && (() => {
                const psychomotor = getTraitObject(result, 'psychomotorSkills', 'PsychomotorSkills');
                const affective = getTraitObject(result, 'affectiveTraits', 'AffectiveTraits');
                console.log('=== Display Psychomotor/Affective ===');
                console.log('psychomotor object:', JSON.stringify(psychomotor));
                console.log('affective object:', JSON.stringify(affective));
                
                return (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B5E20', mb: 2 }}>
                    {isKGStudent ? 'Affective / Psychomotor Assessment' : 'Psychomotor Skills & Affective Traits'}
                  </Typography>

                  {isKGStudent ? (
                    /* KG Assessment Items - single card with all 24 items */
                    <Card sx={{ borderRadius: 2, border: '2px solid #1B5E20' }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          {KG_ASSESSMENT_ITEMS.map((item) => {
                            const psychomotor = getTraitObject(result, 'psychomotorSkills', 'PsychomotorSkills');
                            const affective = getTraitObject(result, 'affectiveTraits', 'AffectiveTraits');
                            // Try both psychomotor and affective sources since KG items can come from either
                            const value = getTraitValue(psychomotor, item.key) || getTraitValue(affective, item.key) || '-';
                            return (
                              <Grid item xs={12} sm={6} md={4} key={item.key + item.label}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ color: '#555', fontSize: '12px' }}>
                                    {item.label}
                                  </Typography>
                                  <Chip
                                    label={value}
                                    size="small"
                                    sx={{
                                      bgcolor: value !== '-' ? '#E8F5E9' : '#F5F5F5',
                                      color: value !== '-' ? '#1B5E20' : '#757575',
                                      fontWeight: 700,
                                    }}
                                  />
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  ) : (
                    /* Regular Psychomotor & Affective - two cards side by side */
                    <Grid container spacing={3}>
                      {/* Psychomotor Skills */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: 2, border: '2px solid #7B1FA2' }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7B1FA2', mb: 2 }}>
                              Psychomotor Skills
                            </Typography>
                            <Grid container spacing={2}>
                              {[
                                { label: 'Drawing & Painting', key: 'drawingPainting' },
                                { label: 'Games & Sports', key: 'gamesSports' },
                                { label: 'Handwriting', key: 'handwriting' },
                                { label: 'Verbal Fluency', key: 'verbalFluency' },
                                { label: 'Musical Skills', key: 'musicalSkills' },
                                { label: 'Handling Tools', key: 'handlingTools' },
                              ].map((item) => {
                                const psychomotor = getTraitObject(result, 'psychomotorSkills', 'PsychomotorSkills');
                                const value = getTraitValue(psychomotor, item.key) || '-';
                                return (
                                  <Grid item xs={12} sm={6} key={item.key}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ color: '#555' }}>
                                        {item.label}
                                      </Typography>
                                      <Chip
                                        label={value}
                                        size="small"
                                        sx={{
                                          bgcolor: value !== '-' ? '#EDE7F6' : '#F5F5F5',
                                          color: value !== '-' ? '#7B1FA2' : '#757575',
                                          fontWeight: 700,
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                      {/* Affective Traits */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: 2, border: '2px solid #00796B' }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#00796B', mb: 2 }}>
                              Affective Traits
                            </Typography>
                            <Grid container spacing={2}>
                              {[
                                { label: 'Punctuality', key: 'punctuality' },
                                { label: 'Politeness', key: 'politeness' },
                                { label: 'Perseverance', key: 'perseverance' },
                                { label: 'Honesty', key: 'honesty' },
                                { label: 'Health', key: 'health' },
                                { label: 'Helping Others', key: 'helpingOthers' },
                                { label: 'Attentiveness', key: 'attentiveness' },
                                { label: 'Emotional Stability', key: 'emotionalStability' },
                                { label: 'Attitude to Class Work', key: 'attitudeToClassWork' },
                                { label: 'Speaking/Handwriting', key: 'speakingHandwriting' },
                                { label: 'Spirit of Cooperation', key: 'spiritOfCooperation' },
                              ].map((item) => {
                                const affective = getTraitObject(result, 'affectiveTraits', 'AffectiveTraits');
                                const value = getTraitValue(affective, item.key) || '-';
                                return (
                                  <Grid item xs={12} sm={6} key={item.key}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ color: '#555' }}>
                                        {item.label}
                                      </Typography>
                                      <Chip
                                        label={value}
                                        size="small"
                                        sx={{
                                          bgcolor: value !== '-' ? '#E0F2F1' : '#F5F5F5',
                                          color: value !== '-' ? '#00796B' : '#757575',
                                          fontWeight: 700,
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              );
              })()}

              {/* Published Status */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Chip
                  label={result.isPublished || result.IsPublished ? 'Result Published' : 'Result Not Published'}
                  size="small"
                  sx={{
                    bgcolor: (result.isPublished || result.IsPublished) ? '#E8F5E9' : '#FFF3E0',
                    color: (result.isPublished || result.IsPublished) ? '#2E7D32' : '#F57C00',
                    fontWeight: 600,
                  }}
                />
                {(result.publishedAt || result.PublishedAt) && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#78909C' }}>
                    Published on: {new Date(result.publishedAt || result.PublishedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* No Result Found */}
        {!error && !result && !loading && (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
            <Typography variant="h6" sx={{ color: '#78909C', mb: 1 }}>
              No Result Found
            </Typography>
            <Typography variant="body2" sx={{ color: '#B0BEC5', mb: 3 }}>
              No results found for this student in the selected term. The student may need to complete exams first.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`${basePath}/results`)}
              sx={{
                bgcolor: '#2E7D32',
                '&:hover': { bgcolor: '#1B5E20' },
              }}
            >
              Back to Results
            </Button>
          </Paper>
        )}
      </Container>

      {/* Psychomotor & Affective Traits Modal */}
      <Dialog
        open={psychomotorModalOpen}
        onClose={() => setPsychomotorModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 700 }}>
          {isKGStudent ? 'Affective / Psychomotor Assessment' : 'Psychomotor Skills & Affective Traits'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: '#757575', mb: 3 }}>
            {isKGStudent
              ? 'Rate each assessment item with grades A-D (A = Excellent, D = Fair)'
              : 'Rate each trait with grades A-F (A = Excellent, F = Needs Improvement)'}
          </Typography>

          {isKGStudent ? (
            /* KG Assessment Items - all 24 items in one table-like layout */
            <Grid container spacing={2}>
              {KG_ASSESSMENT_ITEMS.map((item) => {
                const value = psychomotorData[item.key] || affectiveData[item.key] || '';
                return (
                  <Grid item xs={12} sm={6} md={4} key={item.key + item.label}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5, fontSize: '12px' }}>
                      {item.label}
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={value}
                      onChange={(e) => {
                        if (item.group === 'psychomotor') {
                          handlePsychomotorChange(item.key, e.target.value);
                        } else {
                          handleAffectiveChange(item.key, e.target.value);
                        }
                      }}
                      displayEmpty
                      sx={{
                        bgcolor: 'white',
                        '& .MuiSelect-select': { py: 1 }
                      }}
                    >
                      <MenuItem value=""><em>Select grade</em></MenuItem>
                      {['A', 'B', 'C', 'D'].map((grade) => (
                        <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <>
              {/* Regular Psychomotor Skills */}
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#7B1FA2', mb: 2 }}>
                Psychomotor Skills
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Drawing & Painting', key: 'drawingPainting' },
                  { label: 'Games & Sports', key: 'gamesSports' },
                  { label: 'Handwriting', key: 'handwriting' },
                  { label: 'Verbal Fluency', key: 'verbalFluency' },
                  { label: 'Musical Skills', key: 'musicalSkills' },
                  { label: 'Handling Tools', key: 'handlingTools' },
                ].map((item) => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Select
                      fullWidth
                      value={psychomotorData[item.key]}
                      onChange={(e) => handlePsychomotorChange(item.key, e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: 'white',
                        '& .MuiSelect-select': { py: 1.5 }
                      }}
                    >
                      <MenuItem value=""><em>Select grade</em></MenuItem>
                      {GRADES.map((grade) => (
                        <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                ))}
              </Grid>

              {/* Regular Affective Traits */}
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#00796B', mb: 2 }}>
                Affective Traits
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Punctuality', key: 'punctuality' },
                  { label: 'Politeness', key: 'politeness' },
                  { label: 'Perseverance', key: 'perseverance' },
                  { label: 'Honesty', key: 'honesty' },
                  { label: 'Health', key: 'health' },
                  { label: 'Helping Others', key: 'helpingOthers' },
                  { label: 'Attentiveness', key: 'attentiveness' },
                  { label: 'Emotional Stability', key: 'emotionalStability' },
                  { label: 'Attitude to Class Work', key: 'attitudeToClassWork' },
                  { label: 'Speaking/Handwriting', key: 'speakingHandwriting' },
                  { label: 'Spirit of Cooperation', key: 'spiritOfCooperation' },
                ].map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.key}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Select
                      fullWidth
                      value={affectiveData[item.key]}
                      onChange={(e) => handleAffectiveChange(item.key, e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: 'white',
                        '& .MuiSelect-select': { py: 1.5 }
                      }}
                    >
                      <MenuItem value=""><em>Select grade</em></MenuItem>
                      {GRADES.map((grade) => (
                        <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPsychomotorModalOpen(false)} sx={{ color: '#757575' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePsychomotorAffective}
            variant="contained"
            disabled={savingPsychomotor}
            startIcon={savingPsychomotor ? <CircularProgress size={18} /> : null}
            sx={{ bgcolor: '#7B1FA2', '&:hover': { bgcolor: '#6A1B9A' } }}
          >
            {savingPsychomotor ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden Template for Download */}
      {result && (
        <Box sx={{ position: 'absolute', left: -9999, top: 0 }}>
          <Box ref={downloadTemplateRef}>
            {isKGLevel(result) ? (
              <KGResultSheet data={result} readOnly={true} />
            ) : isPrimaryLevel(result) ? (
              <PrimaryResultSheet data={result} readOnly={true} />
            ) : (
              <ResultSheet data={result} readOnly={true} />
            )}
          </Box>
        </Box>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          button {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};

export default StudentResult;
